/**
 * Static fleet reference data — the Beta Tail List.
 * Sourced from backend/beta-api/scripts/data/beta-tail-list.csv
 * (columns: carrier, tail_no, aircraft_type).
 * Used as the fleet source until the live APES GetFleets API is linked, after
 * which fleetService prefers the API response and falls back to this list.
 */
import { FleetRecord } from '../services/fleetService';

/**
 * Compact [carrier, tailNumber, aircraftType] tuples from the Beta Tail List.
 * Mapped into full FleetRecord objects below; fields not present in the source
 * CSV (region, fleetType, network, etops, cat3) default to empty/false.
 */
const BETA_TAIL_LIST: ReadonlyArray<readonly [string, string, string]> = [
  ['ABR', 'EI-HAD', 'A722'],
  ['ABR', 'EI-AZB', 'B738'],
  ['ABR', 'EI-AZG', 'B738'],
  ['ABR', 'EI-AZC', 'B738'],
  ['ABR', 'EI-AZE', 'B738'],
  ['ABV', 'G-OASB', 'A722'],
  ['FQA', 'VT-AAZ', 'B738'],
  ['ATN', 'N229AZ', 'B763'],
  ['ATN', 'N1049A', 'B763'],
  ['ATN', 'N351CM', 'B763'],
  ['ATN', 'N491AZ', 'B763'],
  ['ATN', 'N433AZ', 'B763'],
  ['ATN', 'N431AZ', 'B763'],
  ['ATN', 'N619AZ', 'B763'],
  ['ATN', 'N313AZ', 'B763'],
  ['ATN', 'N499AZ', 'B763'],
  ['ATN', 'N233AZ', 'B763'],
  ['ATN', 'N241AZ', 'B763'],
  ['ATN', 'N389AZ', 'B763'],
  ['ATN', 'N307AZ', 'B763'],
  ['ATN', 'N367AZ', 'B763'],
  ['ATN', 'N251AZ', 'B763'],
  ['ATN', 'N337AZ', 'B763'],
  ['ATN', 'N317AZ', 'B763'],
  ['ATN', 'N257AZ', 'B763'],
  ['ATN', 'N409AZ', 'B763'],
  ['ATN', 'N397AZ', 'B763'],
  ['ATN', 'N607AZ', 'B763'],
  ['ATN', 'N613AZ', 'B763'],
  ['ATN', 'N353AZ', 'B763'],
  ['ATN', 'N617AZ', 'B763'],
  ['ATN', 'N347AZ', 'B763'],
  ['CJT', 'C-GAZF', 'B763'],
  ['CJT', 'C-FCPD', 'B763'],
  ['ABX', 'N1327A', 'B763'],
  ['ABX', 'N1709A', 'B763'],
  ['ABX', 'N1229A', 'B763'],
  ['ABX', 'N1321A', 'B763'],
  ['ABX', 'N1489A', 'B763'],
  ['CJT', 'C-FNPJ', 'B763'],
  ['CSB', 'N1409A', 'B763'],
  ['CSB', 'N1487A', 'B763'],
  ['CSB', 'N1373A', 'B763'],
  ['ABX', 'N1013A', 'B763'],
  ['ABX', 'N1093A', 'B763'],
  ['CSB', 'N999YV', 'B763'],
  ['CSB', 'N1181A', 'B763'],
  ['CSB', 'N1399A', 'B763'],
  ['CSB', 'N1381A', 'B763'],
  ['ABX', 'N1361A', 'B763'],
  ['ABX', 'N745AX', 'B762'],
  ['ASA', 'N5897K', 'A330'],
  ['ASA', 'N5843K', 'A330'],
  ['ASA', 'N4787K', 'A330'],
  ['ASA', 'N4649K', 'A330'],
  ['ASA', 'N4663K', 'A330'],
  ['ASA', 'N5881K', 'A330'],
  ['ASA', 'N5879K', 'A330'],
  ['ASA', 'N5869K', 'A330'],
  ['ASA', 'N5827K', 'A330'],
  ['ASA', 'N4621K', 'A330'],
  ['ATN', 'N761CX', 'B762'],
  ['ATN', 'N255CM', 'B762'],
  ['ATN', 'N643AZ', 'B763'],
  ['ATN', 'N311AZ', 'B763'],
  ['ATN', 'N227AZ', 'B763'],
  ['SCX', 'N5167A', 'B738'],
  ['SCX', 'N7949A', 'B738'],
  ['SCX', 'N5147A', 'B738'],
  ['SCX', 'N5227A', 'B738'],
  ['SCX', 'N5209A', 'B738'],
  ['SCX', 'N5261A', 'B738'],
  ['SCX', 'N5113A', 'B738'],
  ['SCX', 'N5693A', 'B738'],
  ['SCX', 'N7919A', 'B738'],
  ['SCX', 'N8059A', 'B738'],
  ['SCX', 'N5179A', 'B738'],
  ['SCX', 'N7901A', 'B738'],
  ['SCX', 'N5233A', 'B738'],
  ['SCX', 'N5683A', 'B738'],
  ['SCX', 'N5153A', 'B738'],
  ['SCX', 'N7907A', 'B738'],
  ['SCX', 'N5237A', 'B738'],
  ['ATN', 'N359AZ', 'B763'],
  ['ATN', 'N479AZ', 'B763'],
  ['ATN', 'N1439A', 'B763'],
  ['ATN', 'N457AZ', 'B763'],
  ['ATN', 'N449AZ', 'B763'],
];

/**
 * The Beta Tail List as full FleetRecord objects, ready to back the
 * fleetService helpers (getTailNumbers, getOperatorByTail, getAircraftTypeByTail).
 */
export const STATIC_FLEET: FleetRecord[] = BETA_TAIL_LIST.map(
  ([operator, registrationNumber, aircraftType], index): FleetRecord => ({
    id: index + 1,
    region: '',
    fleetType: '',
    registrationNumber,
    aircraftType,
    operator,
    network: '',
    etops: false,
    cat3: false,
  }),
);
